import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Launch as LaunchIcon,
  Event as EventIcon,
  EventAvailable as EventAvailableIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/Auth0Context';
import { handleNotificationClick, getNotificationActionDescription } from '../../utils/notificationNavigation';
import { useNotifications } from '../../context/NotificationContext';

const NotificationCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Use the centralized notification context instead of hook
  const { 
    notifications, 
    unreadCount, 
    loading, 
    fetchNotifications, 
    markAsRead, 
    markAllAsRead 
  } = useNotifications();

  const open = Boolean(anchorEl);

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'wps_alert':
        return <WarningIcon color="warning" />;
      case 'admin_wps_alert':
        return <ErrorIcon color="error" />;
      case 'wps_daily_countdown':
        return <WarningIcon color="warning" />;
      case 'admin_wps_critical':
        return <ErrorIcon color="error" />;
      case 'wps_monthly_summary':
        return <InfoIcon color="info" />;
      case 'admin_wps_monthly_summary':
        return <InfoIcon color="info" />;
      case 'technical_meeting_reminder':
        return <EventIcon color="primary" />;
      case 'admin_technical_meeting_alert':
        return <EventAvailableIcon color="warning" />;
      case 'wps_created':
        return <CheckCircleIcon color="success" />;
      case 'wps_actual_hours_updated':
        return <WarningIcon color="warning" />;
      case 'info':
        return <InfoIcon color="info" />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  // Parse notification title and content from message
  const parseNotification = (notification) => {
    if (!notification.message) return { title: 'Notification', content: '' };

    // For notification bell, show clean type-based messages and titles
    const getCleanNotificationData = (type) => {
      switch (type) {
        case 'wps_daily_countdown':
          return {
            title: 'WPS Update Reminder',
            content: 'WPS entries need actual hours updated'
          };
        case 'wps_created':
          return {
            title: 'WPS Created',
            content: 'New WPS entry created'
          };
        case 'wps_actual_hours_updated':
          return {
            title: 'WPS Updated',
            content: 'WPS actual hours updated'
          };
        case 'wps_monthly_summary':
          return {
            title: 'Monthly Summary',
            content: 'Monthly WPS summary available'
          };
        case 'technical_meeting_reminder':
          return {
            title: 'Meeting Reminder',
            content: 'Technical meeting reminder'
          };
        case 'wps_alert':
          return {
            title: 'WPS Alert',
            content: 'WPS alert notification'
          };
        case 'admin_wps_alert':
          return {
            title: 'Admin Alert',
            content: 'Admin WPS alert'
          };
        default:
          return {
            title: 'Notification',
            content: 'New notification'
          };
      }
    };

    // Return clean, short titles and content for notification bell
    return getCleanNotificationData(notification.type);
  };

  // Format notification time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Handle menu open
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  // Handle menu close
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ mr: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            borderRadius: '12px',
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={markAllAsRead}
                startIcon={<MarkEmailReadIcon />}
                sx={{ fontSize: '0.75rem' }}
              >
                Mark all read
              </Button>
            )}
          </Box>
          {unreadCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Loading notifications...
              </Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification, index) => {
                const { title, content } = parseNotification(notification);
                return (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        bgcolor: notification.read ? 'rgba(76, 175, 80, 0.05)' : 'rgba(25, 118, 210, 0.08)',
                        '&:hover': {
                          bgcolor: notification.read ? 'rgba(76, 175, 80, 0.1)' : 'rgba(25, 118, 210, 0.12)'
                        },
                        py: 1.5,
                        borderLeft: notification.read ? '3px solid #4caf50' : '3px solid #1976d2',
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={() => {
                        console.log('🖱️ Notification clicked:', notification.id, 'Read status:', notification.read);
                        handleNotificationClick(notification, navigate, markAsRead, user);
                        handleClose(); // Close the menu after clicking
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getNotificationIcon(notification.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: notification.read ? 'normal' : 'bold',
                                fontSize: '0.875rem',
                                color: notification.read ? 'text.secondary' : 'text.primary'
                              }}
                            >
                              {title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatTime(notification.created_at)}
                              </Typography>
                              <Tooltip title={getNotificationActionDescription(notification)}>
                                <LaunchIcon sx={{ fontSize: 14, color: 'action.active', opacity: 0.7 }} />
                              </Tooltip>
                              <Chip
                                label={notification.read ? "Read" : "New"}
                                size="small"
                                color={notification.read ? "success" : "primary"}
                                variant={notification.read ? "outlined" : "filled"}
                                sx={{ height: 16, fontSize: '0.6rem' }}
                              />
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color={notification.read ? 'text.secondary' : 'text.primary'}
                            sx={{
                              mt: 0.5,
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {content}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < notifications.length - 1 && (
                      <Divider sx={{ my: 0 }} />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button
                size="small"
                onClick={() => {
                  navigate('/notifications');
                  handleClose();
                }}
                sx={{ fontSize: '0.75rem' }}
              >
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationCenter;
